'use client'

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { api, useAuth } from './AuthContext'

const WishlistContext = createContext(undefined)

export const useWishlist = () => {
  const ctx = useContext(WishlistContext)
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider')
  return ctx
}

export const WishlistProvider = ({ children }) => {
  const { token } = useAuth()
  const [wishlist, setWishlist] = useState([])
  const [wishlistCount, setWishlistCount] = useState(0)

  const refreshWishlist = useCallback(async () => {
    if (!token) {
      setWishlist([])
      setWishlistCount(0)
      return
    }
    try {
      const res = await api.get('/wishlist')
      setWishlist(res.data || [])
      setWishlistCount(res.data?.length || 0)
    } catch (error) {
      console.error('Failed to fetch wishlist:', error)
      setWishlist([])
      setWishlistCount(0)
    }
  }, [token])

  const addToWishlist = useCallback(async (productId) => {
    if (!token) return false
    try {
      await api.post('/wishlist', { product_id: productId })
      await refreshWishlist()
      return true
    } catch (error) {
      console.error('Failed to add to wishlist:', error)
      return false
    }
  }, [token, refreshWishlist])

  const removeFromWishlist = useCallback(async (productId) => {
    if (!token) return false
    try {
      await api.delete(`/wishlist/${productId}`)
      await refreshWishlist()
      return true
    } catch (error) {
      console.error('Failed to remove from wishlist:', error)
      return false
    }
  }, [token, refreshWishlist])

  const isInWishlist = useCallback((productId) => {
    return wishlist.some(item => item.id === productId)
  }, [wishlist])

  useEffect(() => {
    refreshWishlist()
  }, [refreshWishlist])

  return (
    <WishlistContext.Provider value={{ 
      wishlist, 
      wishlistCount, 
      refreshWishlist, 
      addToWishlist, 
      removeFromWishlist,
      isInWishlist 
    }}>
      {children}
    </WishlistContext.Provider>
  )
}
